import { useState } from "react";
import { Panel, Label } from "@/components/SplitLayout";
import { Globe } from "@phosphor-icons/react";

const Bar = ({ label, value, max, warnOver }) => {
  const pct = Math.min(100, (value / max) * 100);
  const over = value > max;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-bold uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
        <span className={`font-mono font-bold ${over ? "text-accent" : "text-muted-foreground"}`}>
          {value}/{max}
        </span>
      </div>
      <div className="h-1.5 w-full bg-border">
        <div className={`h-full ${over ? "bg-accent" : "bg-primary"}`} style={{ width: `${pct}%` }} />
      </div>
      {over && warnOver && <p className="text-xs text-accent">{warnOver}</p>}
    </div>
  );
};

export default function SerpPreview() {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [desc, setDesc] = useState("");

  const displayUrl = (url || "https://www.example.com/page").replace(/^https?:\/\//, "");
  const crumb = displayUrl.split("/").filter(Boolean);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-6">
        <Panel title="Snippet details">
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Title tag</Label>
                <span className={`font-mono text-xs ${title.length > 60 ? "text-accent" : "text-muted-foreground"}`}>{title.length}/60</span>
              </div>
              <input data-testid="serp-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Your page title…" className="w-full border border-border bg-card px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <input data-testid="serp-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.example.com/page" className="w-full border border-border bg-card px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Meta description</Label>
                <span className={`font-mono text-xs ${desc.length > 160 ? "text-accent" : "text-muted-foreground"}`}>{desc.length}/160</span>
              </div>
              <textarea data-testid="serp-description" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="The description shown under your title in search results…" className="h-28 w-full resize-none border border-border bg-card px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary" />
            </div>
          </div>
        </Panel>
        <Panel title="Length checks">
          <div className="space-y-5">
            <Bar label="Title" value={title.length} max={60} warnOver="Titles over 60 chars may be truncated by Google." />
            <Bar label="Description" value={desc.length} max={160} warnOver="Descriptions over 160 chars may be cut off." />
          </div>
        </Panel>
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start">
        <Panel title="Google preview" testId="serp-output">
          <div className="bg-white p-5 dark:bg-zinc-900">
            <div className="flex items-center gap-2">
              <span className="grid h-6 w-6 place-items-center rounded-full border border-border bg-background text-muted-foreground">
                <Globe size={13} />
              </span>
              <div className="leading-tight">
                <div className="text-[13px] text-zinc-800 dark:text-zinc-200">{crumb[0] || "www.example.com"}</div>
                <div className="font-mono text-xs text-zinc-500">
                  {crumb.length ? crumb.join(" › ") : "https://www.example.com › page"}
                </div>
              </div>
            </div>
            <h3 className="mt-2 text-xl leading-snug text-[#1a0dab] dark:text-[#8ab4f8]">
              {(title || "Your page title appears here").slice(0, 70)}
            </h3>
            <p className="mt-1 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
              {(desc || "Your meta description preview appears here. Write a compelling summary to improve click-through rate from search results.").slice(0, 170)}
            </p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
